<script setup lang="ts">
  import { SessionStore } from '~/stores/session'
  import type { StateMachineContext } from '~/models/StateMachineModels'
  import { SignUpResult } from '~/generated/api-clients-generated'
  import type { HpForm } from '#components'
  import { ProfileStore } from '~/stores/profile'
  import { EngineStore } from '~/stores/engine'
  import type { PushToSaveScenarios } from '~/composables/PushToSave'

  const props = defineProps<{
    context: StateMachineContext
    presentation: string
  }>()

  const { signUp } = Auth.use()
  const session = SessionStore.use()
  const profileStore = ProfileStore.use()
  const engine = EngineStore.use()
  const rxStore = RxStore.use()
  const providerStore = ProviderStore.use()
  const pharmacyStore = PharmacyStore.use()
  const { isGhostCardActive } = GhostCard.use()

  const scenario = computed(() => (props.context?.config ?? 'add-needs') as PushToSaveScenarios)
  const backRoute = props.context.data.get('backRoute')
  const formConfig = computed(() => props.context.data.get('formConfig'))

  const { data } = await PushToSaveContent.use(scenario.value)
  const { track } = Analytics.use()

  const screen = computed(() => data.value?.screens?.signUp)

  const form = ref<InstanceType<typeof HpForm> | null>(null)
  const loading = ref<boolean>(false)

  const { signUpStep1Validation } = AccountValidation.use(undefined, !!screen.value?.showNameFields)

  const flowType = computed(() =>
    props.context.data.get('skipPhoneCapture') ? 'single-part-flow' : 'two-part-flow'
  )

  const onSubmit = async () => {
    loading.value = true
    const response = await signUp(true, null, props.context.data.get('source'), {
      type: flowType.value,
      path: window.location.pathname,
      scenario: _camelCase(scenario.value)
    })

    if (response.result === SignUpResult.AlreadyExists) {
      form.value?.setErrors({
        'account-identifier': screen.value?.errors?.accountExists ?? 'An error occurred'
      })
    } else if (response.result === SignUpResult.Error) {
      form.value?.setErrors({ 'account-identifier': 'An error occurred' })
    } else {
      await SessionStore.load()
      await profileStore.load(profileStore.id!)
      track('PushToSaveAccountCreated', {
        scenario: _camelCase(scenario.value),
        type: flowType.value
      })
      props.context.next()
    }
    loading.value = false
  }

  const onHasAccount = () => {
    props.context.goTo('signIn')
  }

  const reasonsToSave = computed(() => {
    const drugCount = rxStore?.rxs?.length ?? 0
    const docCount = providerStore?.providers?.length ?? 0
    const pharmCount = pharmacyStore?.pharmacies?.length ?? 0
    const benefitCount = profileStore?.needsAssessment?.additionalBenefits?.length ?? 0
    const savedCount =
      profileStore.savedPlans[session.planYear ?? new Date().getFullYear()]?.length ?? 0
    const isPlural = (n: number) => n > 1

    const list = []

    if (drugCount)
      list.push({ amount: drugCount, text: `Prescription${isPlural(drugCount) ? 's' : ''} Added` })
    if (docCount)
      list.push({ amount: docCount, text: `Doctor${isPlural(docCount) ? 's' : ''} Added` })
    if (pharmCount)
      list.push({ amount: pharmCount, text: `Pharmac${isPlural(pharmCount) ? 'ies' : 'y'} Added` })
    if (benefitCount)
      list.push({
        amount: benefitCount,
        text: `Additional Benefit${isPlural(benefitCount) ? 's' : ''} Selected`
      })
    if (savedCount)
      list.push({
        amount: savedCount,
        text: `Saved Plan${isPlural(savedCount) ? 's' : ''}`
      })

    if (engine.displayedQuotes.length && !isGhostCardActive.value)
      list.push({ text: 'Recommendation Received' })

    return list
  })
</script>

<template>
  <div v-if="!_isNil(backRoute)" class="mt-[-30px] flex justify-start">
    <HpAnchor class="hover-bg-active" @click.prevent="props.context.goTo(backRoute)">
      <HpIcon name="arrow-left" />
    </HpAnchor>
  </div>

  <div class="mb-2 grid place-items-center gap-2 text-center">
    <span class="text-5xl leading-none">{{ screen?.icon }}</span>
    <h2 class="text-primary-900">{{ screen?.heading }}</h2>
    <p
      v-for="text in screen?.text"
      :key="text"
      v-html="text"
      class="mb-3 text-balance leading-snug"
    ></p>

    <ul
      v-if="screen?.showReasonsToSave"
      class="mx-auto mb-5 mt-2 flex flex-wrap justify-center gap-3"
    >
      <li
        v-for="item in reasonsToSave"
        :key="item.text"
        class="subtle-shadow-card w-full rounded-3xl border border-border bg-white px-4 pb-0.5 pt-1.5 text-center font-semibold leading-tight xs:w-fit xs:rounded-full"
      >
        <HpIcon
          name="check-v2"
          class="mx-auto mb-1 size-6 rounded-full bg-success-light p-0.5 pb-1 text-success xs:mr-2 xs:inline"
        />
        <span>
          <span v-if="item?.amount">{{ item?.amount }}</span>
          {{ item?.text }}
        </span>
      </li>
    </ul>
  </div>

  <HpForm
    id="sign-up-form"
    ref="form"
    :validation-schema="signUpStep1Validation"
    :on-submit="onSubmit"
    class="flex w-full max-w-xl flex-col items-start gap-y-3"
  >
    <div
      v-if="screen?.showNameFields"
      :class="[
        {
          'flex w-full flex-col gap-x-2 gap-y-3 min-[380px]:flex-row': !formConfig?.singleLineName,
          contents: !!formConfig?.singleLineName
        }
      ]"
    >
      <HpFormInput
        id="account-first-name"
        :label="data?.labels?.firstName"
        :placeholder="data?.labels?.firstName"
        v-model="session.authBag.firstName"
        class="w-full"
      />
      <HpFormInput
        id="account-last-name"
        :label="data?.labels?.lastName"
        :placeholder="data?.labels?.lastName"
        v-model="session.authBag.lastName"
        class="w-full"
      />
    </div>

    <HpFormInput
      id="account-identifier"
      :label="data?.labels?.identifier"
      :placeholder="data?.labels?.identifier"
      v-model="session.authBag.identifier"
      class="w-full text-left"
    />

    <HpButton
      class="mx-auto mt-4 max-w-80"
      :busy="loading"
      :icon="screen?.buttonIcon"
      :icon-location="screen?.buttonIconLocation"
    >
      {{ screen?.cta ?? 'Submit' }}
    </HpButton>
  </HpForm>

  <p class="mt-4 max-w-xl text-balance text-center leading-snug text-primary-900">
    {{ screen?.signInOption?.text }}
    <HpAnchor @click.prevent="onHasAccount" class="font-semibold">
      {{ screen?.signInOption?.cta }}
    </HpAnchor>
  </p>
</template>

<style scoped></style>
